import { Box } from '@pco/atoms/Box';
import { Media } from '@pco/atoms/Media';
import { Separator } from '@pco/atoms/Separator';
import { Text } from '@pco/atoms/Text';
import { Typography } from '@pco/atoms/Typography';
import type { CMSImage } from '@pco/modules/CategoriesNavigation/index.types';
import { type CmsCTAProps, CTAButton } from '@pco/molecules/CTAButton';
import { DetailedCard } from '@pco/molecules/DetailedCard';
import type { ProductCardView } from '@pco/molecules/ProductCard/index.types';
import { ProductPrice } from '@pco/molecules/ProductPrice';
import { useAltImageStore } from '@pco/stores/alt-text.store';
import { getDataAttrs } from '@pco/utils';
import { useCurrentDealer } from '@pco/utils/dealership';
import {
	getProductBrand,
	getProductMainImage,
	getProductPartNumber,
} from '@pco/utils/product';
import styled from 'styled-components';
import { ProductNumber } from '../ProductCard/components/ProductNumber';

const dataAttrs = getDataAttrs(['molecule', 'card-cart']);
const ImageContent = styled(Media)`
  border-radius:  ${({ theme }) => theme.semanticTokens.radius.cards};
	width: 80px;
	height: 80px;
	object-fit: cover;
`;
export interface CartDrawerCardProps {
	loginOrCreateAccount: string;
	cardTitle?: string;
	cardUpdateTitle?: string;
	subtotal?: string;
	mainCTA?: CmsCTAProps;
	secondaryCTA?: CmsCTAProps;
	quantityLabel: string;
	subTotalLabel?: string;
	placeholderImage?: CMSImage;
}

export function CartDrawerCard({
	productView,
	mainCTA,
	secondaryCTA,
	quantity,
	quantityLabel,
	placeholderImage,
}: CartDrawerCardProps & {
	productView: ProductCardView;
	quantity: number;
}) {
	const { isNational } = useCurrentDealer();
	const { generateAltText } = useAltImageStore();
	const image =
		getProductMainImage(productView.images)?.url ??
		placeholderImage?.desktop?.src;
	const title = productView.name;
	const brand = getProductBrand(productView?.attributes);
	const partNumber = getProductPartNumber(productView?.attributes);
	if (productView == null) return <div>Error getting product</div>;
	const { msrp, currentPrice, currency } = {
		msrp: productView.price?.regular?.amount?.value,
		currentPrice: productView.price?.final?.amount?.value,
		currency: productView.price?.regular?.amount?.currency,
	};

	return (
		<Box flexDirection="column" gap="40px" {...dataAttrs('root')()}>
			<Box
				flexDirection="row"
				gap="12px"
				alignItems="center"
				{...dataAttrs('body')()}
			>
				<ImageContent
					image={{
						defaultErrorSrc: placeholderImage?.desktop?.src,
						desktop: {
							src: image,
							alt: generateAltText(image || '', title || ''),
						},
					}}
					{...dataAttrs('image')()}
				/>
				<Box flexDirection="column">
					<ProductNumber
						brand={brand}
						sku={productView.type === 'simple' ? (partNumber ?? '') : ''}
					/>
					<DetailedCard.Title>{title}</DetailedCard.Title>
					<Box flexDirection="row" gap="4px" alignItems="center">
						<Typography _size="body12">
							<Text bindings={{ quantity: `${quantity}` }}>
								{quantityLabel}
							</Text>
						</Typography>
						<Separator orientation="vertical" _height="15px" />
						{msrp != null && (
							<ProductPrice
								price={currentPrice}
								msrp={msrp}
								currency={currency}
								isLight={true}
								isNational={isNational}
							/>
						)}
					</Box>
				</Box>
			</Box>
			<Box gap="20px" flexDirection="column">
				<CTAButton
					_size="lg"
					{...mainCTA}
					fallbackLabel="View cart and checkout"
					data-product-added={productView}
				>
					{mainCTA?.label}
				</CTAButton>
				<CTAButton
					_size="lg"
					{...secondaryCTA}
					fallbackLabel="Continue shopping"
					data-product-added={productView}
				>
					{secondaryCTA?.label}
				</CTAButton>
			</Box>
		</Box>
	);
}
